import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useApi } from '../context/ApiProvider';
import { apiFetch } from '../lib/api';
import { uploadCareerApplicationFiles } from '../lib/careers/fileStorage';
import {
  type CareerJob,
  type CareerJobDraft,
  type CareerWorkType,
} from '../lib/careers/registry';

const PUBLIC_JOB_ENDPOINTS = [
  '/api/careers/jobs',
  '/api/public/careers/jobs',
  '/api/jobs/public',
] as const;

const ADMIN_JOB_ENDPOINTS = [
  '/admin/jobs',
  '/admin/careers/jobs',
] as const;

const APPLICATION_ENDPOINTS = [
  (jobId: string) => `/api/careers/jobs/${encodeURIComponent(jobId)}/apply`,
  () => '/api/careers/apply',
  () => '/api/public/careers/apply',
] as const;

function asText(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function asTextArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => asText(item))
      .filter((item): item is string => Boolean(item));
  }

  const text = asText(value);
  if (!text) return [];
  return text
    .split(/\r?\n|;/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseApiErrorMessage(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const payload = error.response?.data as
    | {
        message?: string | string[];
        error?: string;
      }
    | undefined;
  const rawMessage = payload?.message;
  if (Array.isArray(rawMessage)) {
    const normalized = rawMessage.map((item) => String(item).trim()).filter(Boolean);
    if (normalized.length) return normalized.join(', ');
  }
  if (typeof rawMessage === 'string' && rawMessage.trim()) return rawMessage.trim();
  if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error.trim();
  if (typeof error.message === 'string' && error.message.trim()) return error.message.trim();
  return null;
}

function coerceWorkType(value: unknown): CareerWorkType {
  const text = asText(value)?.toLowerCase() ?? '';
  if (text.includes('part')) return 'part_time';
  if (text.includes('contract')) return 'contract';
  return 'full_time';
}

function coerceTargetHireCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const rounded = Math.round(value);
    if (rounded < 1) return 1;
    if (rounded > 99) return 99;
    return rounded;
  }
  const text = asText(value);
  if (!text) return 1;
  const parsed = Number.parseInt(text, 10);
  if (!Number.isFinite(parsed)) return 1;
  if (parsed < 1) return 1;
  if (parsed > 99) return 99;
  return parsed;
}

function normalizeRemoteJob(rawValue: unknown, index: number): CareerJob | null {
  const raw = (rawValue ?? {}) as Record<string, unknown>;
  const title = asText(raw.title ?? raw.name ?? raw.position);
  if (!title) return null;

  const requisitionId =
    asText(raw.requisitionId ?? raw.requisitionNumber ?? raw.jobId ?? raw.id) ??
    `CYB-REMOTE-${index + 1}`;
  const slugSource = asText(raw.slug) ?? title;
  const slug = slugSource
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const now = Date.now();

  return {
    id: asText(raw.id ?? raw.jobId ?? raw.requisitionId ?? `${slug || 'career'}-${index}`) ?? `remote-${index}`,
    slug: slug || `career-${index + 1}`,
    requisitionId,
    title,
    team: asText(raw.team ?? raw.department) ?? 'General',
    brainNodeId: asText(raw.brainNodeId ?? raw.teamNodeId ?? raw.nodeId) ?? undefined,
    location: asText(raw.location ?? raw.region) ?? 'Remote',
    workType: coerceWorkType(raw.workType ?? raw.employmentType ?? raw.type),
    summary: asText(raw.summary ?? raw.subtitle ?? raw.teaser) ?? title,
    description: asText(raw.description ?? raw.overview) ?? '',
    responsibilities: asTextArray(raw.responsibilities ?? raw.tasks),
    requirements: asTextArray(raw.requirements ?? raw.qualifications),
    published: Boolean(raw.published ?? raw.isPublished ?? true),
    hiringManager:
      asText(
        raw.hiringManager ??
          raw.hiringManagerEmail ??
          raw.hiringManagerName ??
          raw.manager,
      ) ?? '',
    targetHireCount: coerceTargetHireCount(
      raw.targetHireCount ?? raw.targetHires ?? raw.hiringCount,
    ),
    compensation: asText(raw.compensation ?? raw.salary ?? raw.payRange) ?? '',
    createdAt: Number(raw.createdAt ?? now) || now,
    updatedAt: Number(raw.updatedAt ?? raw.modifiedAt ?? now) || now,
    source: 'remote',
  };
}

function extractJobCollection(payload: unknown): CareerJob[] {
  const value = payload as Record<string, unknown> | unknown[];
  const collection = Array.isArray(value)
    ? value
    : (value?.jobs as unknown[]) ??
      (value?.items as unknown[]) ??
      (value?.results as unknown[]) ??
      (value?.data as unknown[]) ??
      [];

  return collection
    .map((item, index) => normalizeRemoteJob(item, index))
    .filter((job): job is CareerJob => Boolean(job));
}

async function fetchRemotePublicJobs(): Promise<CareerJob[]> {
  for (const endpoint of PUBLIC_JOB_ENDPOINTS) {
    try {
      const payload = await apiFetch<unknown>(endpoint);
      const jobs = extractJobCollection(payload);
      if (jobs.length) return jobs;
    } catch {
      // try next endpoint
    }
  }
  return [];
}

export interface CareerApplicationSubmission {
  firstName: string;
  lastName: string;
  email: string;
  contactPhone?: string;
  wasReferred?: boolean;
  referredBy?: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  backgroundCheckAcknowledged: boolean;
  businessWebsite?: string;
  bio?: string;
  linkedInUrl?: string;
  portfolioUrl?: string;
  coverLetterText?: string;
  resumeFile: File;
  coverLetterFile: File;
  job: CareerJob;
}

function buildApplicationFormData(
  payload: CareerApplicationSubmission,
  uploaded: Awaited<ReturnType<typeof uploadCareerApplicationFiles>>,
): FormData {
  const formData = new FormData();
  formData.append('firstName', payload.firstName.trim());
  formData.append('lastName', payload.lastName.trim());
  formData.append('email', payload.email.trim());
  formData.append('contactPhone', (payload.contactPhone ?? '').trim());
  formData.append('customerType', 'individual');
  formData.append('businessName', '');
  formData.append('businessWebsite', (payload.businessWebsite ?? '').trim());
  formData.append('businessAddress', payload.addressLine1.trim());
  formData.append('addressLine1', payload.addressLine1.trim());
  formData.append('city', payload.city.trim());
  formData.append('state', payload.state.trim());
  formData.append('postalCode', payload.postalCode.trim());
  formData.append('pointOfContact', (payload.referredBy ?? '').trim());
  formData.append('wasReferred', payload.wasReferred ? 'yes' : 'no');
  formData.append('referredBy', (payload.referredBy ?? '').trim());
  formData.append(
    'backgroundCheckAcknowledged',
    payload.backgroundCheckAcknowledged ? 'yes' : 'no',
  );
  formData.append('bio', (payload.bio ?? '').trim());
  formData.append('linkedInUrl', (payload.linkedInUrl ?? '').trim());
  formData.append('portfolioUrl', (payload.portfolioUrl ?? '').trim());
  formData.append('coverLetterText', (payload.coverLetterText ?? '').trim());
  formData.append('jobId', payload.job.id);
  formData.append('jobTitle', payload.job.title);
  formData.append('requisitionId', payload.job.requisitionId);
  formData.append('applicationId', uploaded.applicationId);
  formData.append('resumeStoragePath', uploaded.resume.storagePath);
  formData.append('coverLetterStoragePath', uploaded.coverLetter.storagePath);
  formData.append('resumeDownloadUrl', uploaded.resume.downloadUrl);
  formData.append('coverLetterDownloadUrl', uploaded.coverLetter.downloadUrl);
  formData.append('resume', payload.resumeFile, payload.resumeFile.name);
  formData.append('coverLetter', payload.coverLetterFile, payload.coverLetterFile.name);
  return formData;
}

export function useCareerOpenings() {
  return useQuery<CareerJob[]>({
    queryKey: ['careers-jobs-public'],
    queryFn: () => fetchRemotePublicJobs(),
    staleTime: 60 * 1000,
  });
}

export function useCareerOpening(jobSlug: string | undefined) {
  const jobsQuery = useCareerOpenings();
  const job = useMemo(() => {
    const slug = (jobSlug ?? '').trim().toLowerCase();
    if (!slug) return null;

    return jobsQuery.data?.find((item) => item.slug.toLowerCase() === slug) ?? null;
  }, [jobSlug, jobsQuery.data]);

  return {
    ...jobsQuery,
    job,
  };
}

export function usePostCareerJob() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (draft: CareerJobDraft) => {
      let lastError: unknown = null;
      for (const endpoint of ADMIN_JOB_ENDPOINTS) {
        try {
          await api.post(endpoint, draft);
          return {
            remoteSynced: true,
          };
        } catch (error) {
          if (isAxiosError(error)) {
            const status = error.response?.status ?? 0;
            if (status >= 400 && status < 500 && status !== 404) {
              const detail = parseApiErrorMessage(error);
              throw new Error(detail || `Failed to create listing (${status}).`);
            }
          }
          lastError = error;
          // try next endpoint
        }
      }
      const detail = parseApiErrorMessage(lastError);
      if (detail) throw new Error(detail);
      if (lastError instanceof Error) throw lastError;
      throw new Error('Failed to create listing.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-board'] });
      queryClient.invalidateQueries({ queryKey: ['careers-jobs-public'] });
    },
  });
}

export function useSubmitCareerApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CareerApplicationSubmission) => {
      let uploaded: Awaited<ReturnType<typeof uploadCareerApplicationFiles>>;
      try {
        uploaded = await uploadCareerApplicationFiles({
          jobId: payload.job.id,
          requisitionId: payload.job.requisitionId,
          applicantEmail: payload.email,
          resumeFile: payload.resumeFile,
          coverLetterFile: payload.coverLetterFile,
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unable to store application files.';
        throw new Error(
          `Resume/Cover Letter upload failed: ${message}. Check Firebase Storage rules for career-applications uploads.`,
        );
      }

      let remoteSynced = false;
      let remoteCandidateId: string | null = null;

      for (const buildEndpoint of APPLICATION_ENDPOINTS) {
        const endpoint = buildEndpoint(payload.job.id);
        try {
          const response = await apiFetch<unknown>(endpoint, {
            method: 'POST',
            body: buildApplicationFormData(payload, uploaded),
          });
          remoteSynced = true;
          const raw = response as Record<string, unknown> | null;
          remoteCandidateId =
            asText(raw?.candidateId ?? raw?.id ?? raw?.uid ?? null) ?? null;
          break;
        } catch {
          // try next endpoint
        }
      }

      if (remoteSynced) {
        return {
          candidateId: remoteCandidateId ?? `remote-${Date.now()}`,
          remoteSynced: true,
        };
      }
      throw new Error('Application was uploaded but candidate capture failed.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}
